"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import styles from "../recruit.module.scss";

type JobBodyRef = {
  [key: number]: HTMLDivElement | null;
};

const jobs = [
  {
    id: 1,
    title: "ウェブエンジニア Web Engineer",
    tags: ["Next.js", "正社員"],
    description:
      "WordPress / Next.js / Headless CMSを用いたWebサイト制作、および自社プロダクト（MEOツール）の開発を担当していただきます。",
    duties: [
      "Next.jsを用いたサイト構築・パフォーマンスチューニング",
      "microCMSの設計・構築",
      "HubSpot等のMAツールとのAPI連携",
    ],
    requirements: ["React / Next.js 実務経験2年以上", "TypeScriptでの開発経験"],
    salary: "400万 〜 700万円",
    location: "東中野駅徒歩5分",
  },
  {
    id: 2,
    title: "SEOコンサルタント SEO Consultant",
    tags: ["SEO", "PM", "正社員", "未経験可", "インターン可"],
    description:
      "クライアントの課題解決に向けたWebサイトの企画・設計、およびSEOコンサルティングを担当します。",
    duties: [
      "Webサイトの要件定義・ワイヤーフレーム作成",
      "キーワード調査・SEO戦略立案",
      "制作進行管理",
    ],
    requirements: [],
    salary: "400万 〜 700万円",
    location: "東中野駅徒歩5分",
  },
  {
    id: 3,
    title: "広告運用担当者 Ad Planner",
    tags: ["広告運用", "未経験可", "正社員", "インターン可"],
    description:
      "Google / Meta広告などの運用型広告を活用し、クライアントの利益最大化を支援します。",
    duties: [
      "広告アカウントの設計・入稿",
      "日次の予算管理・入札調整",
      "レポート作成・改善提案",
    ],
    requirements: [],
    salary: "400万 〜 700万円",
    location: "東中野駅徒歩5分",
  },
];

const galleryItems = [
  {
    id: 1,
    colSpan: 2,
    rowSpan: 2,
    category: "MEETING",
    text: "毎日朝会で進捗報告。対面でのコミュニケーションを大事にしています。",
    image: "/office.jpg",
  },
  {
    id: 2,
    category: "OFFICE",
    text: "ネスプレッソのコーヒーが飲み放題。",
    image: "/coffee.jpg",
  },
  {
    id: 3,
    category: "WORKSHOP",
    text: "半期に一度の全社戦略会議。",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=500&auto=format&fit=crop",
  },
  {
    id: 4,
    colSpan: 2,
    category: "GROWTH",
    text: "年間10万円まで書籍購入などの学習補助があります。",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=800&auto=format&fit=crop",
  },
  {
    id: 5,
    category: "CULTURE",
    text: "常に最新技術の勉強を行い、顧客への価値提供をアップデートしていきます。",
    image:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=500&auto=format&fit=crop",
  },
  {
    id: 6,
    category: "REMOTE",
    text: "自分好みのデスク環境で生産性UP。",
    image:
      "https://images.unsplash.com/photo-1661956602116-aa6865609028?q=80&w=500&auto=format&fit=crop",
  },
];

const techStack = [
  "Next.js",
  "React",
  "TypeScript",
  "Vercel",
  "microCMS",
  "HubSpot",
  "Figma",
  "GA4",
  "Google Ads",
];

type Benefit = {
  id: string;
  name: string;
  lead: string;
  body: string[];
  verse?: string[];
  listTitle?: string;
  list?: string[];
  footer?: string[];
  wide?: boolean;
};

const benefits: Benefit[] = [
  {
    id: "growth",
    name: "Pyxis Growth Support",
    lead: "顧客成果につながる学びに、会社として投資します。",
    body: [
      "広告、SEO、Web制作、AIなど、私たちが扱う領域は常に変化しています。",
      "新しい知識や技術を学ぶことはもちろん、お客さまの事業や、その先にいるお客さまを理解することも、仕事の一部だと考えています。",
      "業務や顧客成果につながる学習・スキルアップについて、会社が費用を支援します。",
    ],
    listTitle: "対象例",
    list: [
      "書籍の購入",
      "オンライン講座",
      "セミナー、カンファレンスへの参加",
      "資格取得",
      "AIツール、業務ツールの利用",
      "その他、専門性を高めるために必要な学習",
    ],
  },
  {
    id: "client-experience",
    name: "Client Experience Program",
    lead: "お客さまを理解するために、まず自分たちが体験する。",
    body: ["Webサイトの数字や広告管理画面だけでは、分からないことがあります。"],
    verse: [
      "実際に商品を購入する。",
      "店舗へ行く。",
      "サービスを受ける。",
      "予約から利用までの流れを体験する。",
    ],
    footer: [
      "ピクシスでは、現在支援しているクライアントの商品やサービスを実際に体験し、顧客理解を深めることを推奨しています。",
      "業務上必要な顧客体験については、会社が費用の一部または全部を負担します。",
      "体験から得た気づきは、広告、SEO、Webサイト、クリエイティブ、顧客導線などの改善に活かします。",
      "机上のマーケティングだけではなく、一次情報を大切にするための制度です。",
    ],
  },
  {
    id: "health",
    name: "Pyxis Health Support",
    lead: "良い仕事を続けるために、健康を大切にする。",
    body: [
      "高いパフォーマンスを一時的に出すのではなく、長く良い仕事を続けることを大切にしています。",
      "健康診断をはじめ、社員が心身のコンディションを整えるための環境づくりを会社として支援します。",
    ],
  },
  {
    id: "family-leave",
    name: "Pyxis Family Leave",
    lead: "大切なときには、大切なことを優先する。",
    body: [
      "仕事と同じように、家族や身近な人との時間も大切だと考えています。",
      "結婚、出産、子どもの行事、家族の看護や介護など、人生の大切なタイミングで利用できる特別休暇制度を設けています。",
      "「仕事があるから休めない」ではなく、必要なときに安心して休める環境を目指します。",
    ],
  },
  {
    id: "refresh-retreat",
    name: "Pyxis Refresh & Retreat",
    lead: "日常から離れて、仕事と会社について考える時間をつくる。",
    body: [
      "目の前の業務だけを続けていると、新しいアイデアや長期的な視点を持つことが難しくなることがあります。",
      "ピクシスでは、普段とは異なる環境で、会社や顧客、チームについて考える時間を大切にしています。",
      "その取り組みのひとつとして、SANU 2nd Homeなどを活用した社内研修・チームリトリートを行います。",
    ],
    listTitle: "研修テーマ例",
    list: [
      "事業戦略の検討",
      "顧客理解を深めるワークショップ",
      "広告、SEO、AIなどの社内研修",
      "成功事例、失敗事例の共有",
      "新しいサービスや施策の企画",
      "チームでの振り返り",
    ],
    footer: [
      "自然の中で過ごしながら、普段とは少し違った視点で仕事や会社の未来を考える時間をつくります。",
      "また、SANUについては社内研修だけでなく、福利厚生として利用できる環境づくりも進めています。",
    ],
    wide: true,
  },
];

const benefitCriteria = [
  "学ぶこと。",
  "お客さまを知ること。",
  "健康でいること。",
  "大切な人との時間を守ること。",
  "ときには日常を離れて考えること。",
];

export default function RecruitContent() {
  const [activeJob, setActiveJob] = useState<number | null>(null);
  const [counted, setCounted] = useState(false);
  const numbersRef = useRef<HTMLDivElement>(null);
  const jobBodyRefs = useRef<JobBodyRef>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !counted) {
            setCounted(true);
            // Count up animation
            const counters =
              entry.target.querySelectorAll<HTMLElement>("[data-target]");
            counters.forEach((counter) => {
              const target = parseInt(
                counter.getAttribute("data-target")?.replace(/,/g, "") || "0"
              );
              const duration = 2000;
              const start = Date.now();
              const startValue = 0;

              // 数値をカンマ区切りでフォーマットする関数
              const formatNumber = (num: number): string => {
                return num.toLocaleString("ja-JP");
              };

              const animate = () => {
                const now = Date.now();
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                const current = Math.floor(
                  startValue + (target - startValue) * progress
                );
                counter.textContent = formatNumber(current);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                }
              };

              animate();
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    const el = numbersRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [counted]);

  useEffect(() => {
    // アクティブなジョブの高さを設定
    Object.keys(jobBodyRefs.current).forEach((key) => {
      const id = parseInt(key);
      const body = jobBodyRefs.current[id];
      if (body) {
        if (activeJob === id) {
          body.style.height = `${body.scrollHeight}px`;
        } else {
          body.style.height = "0px";
        }
      }
    });
  }, [activeJob]);

  const toggleJob = (id: number) => {
    setActiveJob(activeJob === id ? null : id);
  };

  return (
    <>
      {/* Culture / Values */}
      <section className={styles.contentSection}>
        <h2 className={`${styles.sectionHead} fade-up`}>
          Why Pyxis? (Culture)
        </h2>
        <div className={styles.valuesGrid}>
          <div className={`${styles.valueCard} fade-up`}>
            <span className={styles.valueNum}>01</span>
            <h3 className={styles.valueTitle}>結果主義</h3>
            <p className={styles.valueDesc}>
              KPIやレポートをきれいにするだけでは意味がありません。ピクシスでは、CPAやCV数だけでなく、LTV・粗利・キャッシュフローまで含めて「この施策は本当にクライアントの得になっているか？」を問い続けます。
            </p>
          </div>
          <div className={`${styles.valueCard} fade-up`}>
            <span className={styles.valueNum}>02</span>
            <h3 className={styles.valueTitle}>挑戦者で居続ける</h3>
            <p className={styles.valueDesc}>
              自社サイト・自社プロダクト・自社広告を実験場にし、成功も失敗も含めて最新のナレッジとして蓄えることで、お客様には「実験」ではなく「勝ち筋に近い打ち手」を届けていきます。
            </p>
          </div>
          <div className={`${styles.valueCard} fade-up`}>
            <span className={styles.valueNum}>03</span>
            <h3 className={styles.valueTitle}>うれしい誤算を作る</h3>
            <p className={styles.valueDesc}>
              求められた結果をきっちり出すことは当然。そのうえでピクシスは「ここまでやってくれるなんて」と思ってもらえる一歩先を、いきます。数字の改善、気づきの深さ、スピード、コミュニケーション──どの切り口でもいいから、クライアントにとっての「うれしい誤算」をつくります。
            </p>
          </div>
        </div>
      </section>

      {/* Numbers Section */}
      <section className={styles.contentSection} ref={numbersRef}>
        <h2 className={`${styles.sectionHead} fade-up`}>
          Numbers (数字で見るピクシス)
        </h2>
        <div className={`${styles.numbersGrid} fade-up`}>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="4">0</span>
              <span>名</span>
            </span>
            <span className={styles.numLabel}>社員数</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="42">0</span>
              <span>歳</span>
            </span>
            <span className={styles.numLabel}>平均年齢</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="75">0</span>
              <span>%</span>
            </span>
            <span className={styles.numLabel}>未経験からの入社</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="146">0</span>
              <span>社</span>
            </span>
            <span className={styles.numLabel}>累計支援社数</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="18">0</span>
              <span>h</span>
            </span>
            <span className={styles.numLabel}>月間平均残業時間</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="203">0</span>
              <span>%</span>
            </span>
            <span className={styles.numLabel}>昨対比売上成長率</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="10">0</span>
              <span>万</span>
            </span>
            <span className={styles.numLabel}>書籍購入・学習補助/年</span>
          </div>
          <div className={styles.numItem}>
            <span className={styles.numVal}>
              <span data-target="124998036">0</span>
              <span>円</span>
            </span>
            <span className={styles.numLabel}>年間広告運用額</span>
          </div>
        </div>
      </section>

      {/* Tech Stack Marquee */}
      <section className={styles.techSection}>
        <div className={styles.marqueeWrap}>
          <div className={styles.marqueeContent}>
            {techStack.map((tech, index) => (
              <span key={index} className={styles.techLogo}>
                {tech}
              </span>
            ))}
            {techStack.map((tech, index) => (
              <span key={`duplicate-${index}`} className={styles.techLogo}>
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className={styles.contentSection} style={{ paddingBottom: 0 }}>
        <h2 className={`${styles.sectionHead} fade-up`}>
          Atmosphere (働く風景)
        </h2>

        <div className={styles.galleryGrid}>
          {galleryItems.map((item) => (
            <div
              key={item.id}
              className={`${styles.galleryItem} fade-up ${
                item.colSpan === 2 ? styles.col2 : ""
              } ${item.rowSpan === 2 ? styles.row2 : ""}`}
            >
              <div
                className={styles.galleryImg}
                style={{ backgroundImage: `url(${item.image})` }}
              />
              <div className={styles.galleryOverlay}>
                <span className={styles.galleryCat}>{item.category}</span>
                <p className={styles.galleryTxt}>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits / Internal Programs */}
      <section className={styles.contentSection} id="benefits">
        <h2 className={`${styles.sectionHead} fade-up`}>
          Benefits (福利厚生・社内制度)
        </h2>

        <div className={`${styles.benefitsIntro} fade-up`}>
          <p className={styles.benefitsIntroLead}>
            顧客成果を、
            <br />
            継続的に生み出せる
            <br />
            組織へ。
          </p>
          <div className={styles.benefitsIntroBody}>
            <p>ピクシスでは、福利厚生を単なる待遇の充実とは考えていません。</p>
            <p>
              お客さまの事業を深く理解し、専門性を磨き、良いコンディションで仕事を続けること。その積み重ねが、継続的な顧客成果につながると考えています。
            </p>
            <p>
              そのために、学び、体験し、休み、考えるための環境を会社として整えています。
            </p>
          </div>
        </div>

        <div className={styles.benefitsList}>
          {benefits.map((benefit, index) => {
            const hasAside = Boolean(benefit.verse || benefit.list);
            return (
              <article
                key={benefit.id}
                className={`${styles.benefitRow} fade-up`}
              >
                <div className={styles.benefitIndex}>
                  <span className={styles.benefitNum}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className={styles.benefitName}>{benefit.name}</span>
                </div>

                <div className={styles.benefitMain}>
                  <h3 className={styles.benefitLead}>{benefit.lead}</h3>

                  <div className={styles.benefitCols}>
                    <div className={styles.benefitBody}>
                      {benefit.body.map((text, idx) => (
                        <p key={idx}>{text}</p>
                      ))}
                      {benefit.footer?.map((text, idx) => (
                        <p key={idx}>{text}</p>
                      ))}
                    </div>

                    {hasAside && (
                      <div className={styles.benefitAside}>
                        {benefit.verse && (
                          <ul className={styles.benefitVerse}>
                            {benefit.verse.map((line, idx) => (
                              <li key={idx}>{line}</li>
                            ))}
                          </ul>
                        )}

                        {benefit.list && (
                          <>
                            <h4 className={styles.benefitListTitle}>
                              {benefit.listTitle}
                            </h4>
                            <ul className={styles.benefitChips}>
                              {benefit.list.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className={`${styles.benefitCriteria} fade-up`}>
          <div className={styles.benefitCriteriaHead}>
            <h3 className={styles.benefitCriteriaTitle}>制度をつくる基準</h3>
          </div>

          <div className={styles.benefitCriteriaBody}>
            <p>ピクシスでは、福利厚生の数を増やすことを目的にはしていません。</p>
            <p>制度をつくる際に大切にしているのは、</p>
            <blockquote className={styles.benefitCriteriaQuote}>
              この制度が、顧客成果を継続的に
              <br />
              生み出せる組織づくりにつながるか
            </blockquote>
            <p>ということです。</p>

            <ul className={styles.benefitCriteriaList}>
              {benefitCriteria.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            <p>
              社員一人ひとりが良い状態で仕事に向き合える環境を、会社としてつくっていきます。
            </p>
            <p className={styles.benefitNote}>
              ※各制度の対象者、利用条件、補助内容等は社内規程に基づきます。
            </p>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className={styles.contentSection} id="jobs">
        <h2 className={`${styles.sectionHead} fade-up`}>Open Positions</h2>

        <div className={`${styles.jobList} fade-up`}>
          {jobs.map((job) => (
            <div
              key={job.id}
              className={`${styles.jobItem} ${
                activeJob === job.id ? styles.active : ""
              }`}
            >
              <button
                className={styles.jobHeader}
                onClick={() => toggleJob(job.id)}
              >
                <div>
                  <h3 className={styles.jobTitle}>{job.title}</h3>
                  <div className={styles.jobTags}>
                    {job.tags.map((tag, idx) => (
                      <span key={idx} className={styles.jobTag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <span className={styles.jobIcon}>＋</span>
              </button>
              <div
                ref={(el) => {
                  jobBodyRefs.current[job.id] = el;
                }}
                className={styles.jobBody}
                style={{
                  height: activeJob === job.id ? "auto" : "0px",
                }}
              >
                <div className={styles.jobInner}>
                  <div className={styles.jobDesc}>
                    <p>{job.description}</p>
                    <h4>業務内容</h4>
                    <ul>
                      {job.duties.map((duty, idx) => (
                        <li key={idx}>{duty}</li>
                      ))}
                    </ul>
                    {job.requirements.length > 0 && (
                      <>
                        <h4>必須スキル</h4>
                        <ul>
                          {job.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                  <div className={styles.jobSidebar}>
                    <p>
                      <strong>想定年収</strong>
                      <br />
                      {job.salary}
                    </p>
                    <p>
                      <strong>勤務地</strong>
                      <br />
                      {job.location}
                    </p>
                    <Link href="#" className={`${styles.jobBtn} hover-target`}>
                      この職種に応募する
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Entry CTA */}
      <section className={styles.entryCta} id="entry">
        <div className="fade-up">
          <p className={styles.sectionLabel}>ENTRY</p>
          <h2 className={styles.entryTitle}>まずは話をしてみませんか？</h2>
          <p className={styles.entryCopy}>
            「自分のスキルが通用するか知りたい」「会社の雰囲気を知りたい」
            <br />
            そんな方は、選考要素なしのカジュアル面談へお越しください。
          </p>
          <div className={styles.entryBtns}>
            <Link
              href="/recruit/casual"
              className={`${styles.btnCasual} hover-target`}
            >
              カジュアル面談を申し込む
            </Link>
            <Link
              href="/recruit/entry"
              className={`${styles.btnApply} hover-target`}
            >
              エントリーフォームへ
            </Link>
          </div>
          <p className={styles.entryNote}>
            ※カジュアル面談はオンライン（Google
            Meet）で30分程度を予定しています。
          </p>
        </div>
      </section>
    </>
  );
}
